import { fetchResolveBrowseUrl, type ResolveBrowseUrlApiResponse } from '@/redux/api/browseUrl';
import {
    type BrowseDimensionPair,
    browsePathHasTildeMultiValue,
    buildBrowseCategoryPath,
    mapBrowsePairsToSearchOptionIds,
    parseBrowseTail,
    sortBrowsePairsCanonical,
} from '@/utils/browsePath/browsePath';
import { getTopCategoryIdFromSlug } from '@/utils/browsePath/resolveCategorySlug';

import { mapResolvedCategoryIdsToBrowseFacetOptions } from './queryBuilders';
import { CategoryRouteContext } from './routeContext';

type ResolveBrowseInputsArgs = {
    deployment: string;
    locationSearch: string;
    routeContext: CategoryRouteContext;
    searchFacets: any;
    stateAfterFacets: any;
};

export type BrowseResolutionResult = {
    baseCategoryIds: number[];
    browseFacetOptionIds: Record<string, string[]> | undefined;
    browsePathPairsForPostSearchRemap: BrowseDimensionPair[];
    redirectUrl: string | null;
    shouldAttemptPostSearchBrowseRemap: boolean;
    slugOnlyPathForInvalidBrowsePath: string | null;
};

const hasAnyFacetIds = (facetOptionIds: Record<string, string[]> | undefined) =>
    Boolean(facetOptionIds && Object.values(facetOptionIds).some((ids) => Array.isArray(ids) && ids.length > 0));

/**
 * `search.facets` are global to the last search response; `runPreloadPhase` does not load facets for the incoming URL.
 * Using them to map `/c/{slug}/origin/african/` while `lastBrowseCategorySlug` still refers to a previous category
 * resolves slugs against the wrong taxonomy (or overwrites resolver facet ids during merge).
 */
const searchFacetsApplyToBrowseSlug = (
    stateAfterFacets: { browsePath?: { lastBrowseCategorySlug: string | null } },
    browseCategorySlug: string | undefined
): boolean =>
    Boolean(
        browseCategorySlug &&
        stateAfterFacets?.browsePath?.lastBrowseCategorySlug !== null &&
        stateAfterFacets?.browsePath?.lastBrowseCategorySlug === browseCategorySlug
    );

export const resolveBrowseInputs = async ({
    deployment,
    locationSearch,
    routeContext,
    searchFacets,
    stateAfterFacets,
}: ResolveBrowseInputsArgs): Promise<BrowseResolutionResult> => {
    const {
        browseParsed,
        browseSplat,
        hasRegularLegacyCategory,
        isBrowseSlugRoute,
        isCombinationCategory,
        legacyCategoryId,
        legacyCategoryId2,
        legacyPathParsed,
        pathname,
    } = routeContext;

    let baseCategoryIds: number[] = [];
    let browseFacetOptionIds: Record<string, string[]> | undefined;
    let shouldAttemptPostSearchBrowseRemap = false;
    let browsePathPairsForPostSearchRemap: BrowseDimensionPair[] = [];
    let slugOnlyPathForInvalidBrowsePath: string | null = null;

    if (legacyPathParsed) {
        return {
            baseCategoryIds: [Number(legacyPathParsed.numericCategoryId)],
            browseFacetOptionIds: undefined,
            browsePathPairsForPostSearchRemap: [],
            redirectUrl: null,
            shouldAttemptPostSearchBrowseRemap: false,
            slugOnlyPathForInvalidBrowsePath: null,
        };
    }

    if (isBrowseSlugRoute && routeContext.params?.browseCategorySlug) {
        const slugOnlyPath = browseParsed
            ? buildBrowseCategoryPath(browseParsed.categorySlug, [], { base: browseParsed.base })
            : `/c/${routeContext.params.browseCategorySlug}/`;
        slugOnlyPathForInvalidBrowsePath = slugOnlyPath;

        const pathPairsForBrowse = browseParsed?.pairs?.length > 0 ? browseParsed.pairs : parseBrowseTail(browseSplat);
        const hasMalformedBrowseSplat = Boolean(
            browseSplat && browseSplat.split('/').filter(Boolean).length > 0 && pathPairsForBrowse.length === 0
        );
        const rawFacetsForPathMapping = searchFacetsApplyToBrowseSlug(
            stateAfterFacets,
            routeContext.params.browseCategorySlug
        )
            ? searchFacets
            : [];
        const pathMappedFacets = mapBrowsePairsToSearchOptionIds(
            sortBrowsePairsCanonical(pathPairsForBrowse),
            rawFacetsForPathMapping
        );
        const pathMappedHasIds = hasAnyFacetIds(pathMappedFacets);
        const resolveBrowseUrlSafe = async (path: string): Promise<ResolveBrowseUrlApiResponse> => {
            try {
                return await fetchResolveBrowseUrl({
                    deployment,
                    pathname: path,
                    search: '',
                });
            } catch {
                return { error: true };
            }
        };

        const knownTopCategoryIdFromSlug = getTopCategoryIdFromSlug(routeContext.params.browseCategorySlug);
        if (knownTopCategoryIdFromSlug !== null) {
            baseCategoryIds = [knownTopCategoryIdFromSlug];
            if (hasMalformedBrowseSplat) {
                const querySuffix = locationSearch && locationSearch !== '?' ? locationSearch : '';
                return {
                    baseCategoryIds,
                    browseFacetOptionIds,
                    browsePathPairsForPostSearchRemap,
                    redirectUrl: `${slugOnlyPath}${querySuffix}`,
                    shouldAttemptPostSearchBrowseRemap,
                    slugOnlyPathForInvalidBrowsePath,
                };
            }

            if (pathPairsForBrowse.length > 0 && !pathMappedHasIds && !browsePathHasTildeMultiValue(pathname)) {
                const resolved = await resolveBrowseUrlSafe(pathname);
                const resolvedRedirectPath = resolved.payload?.redirectPath;
                if (
                    !resolved.error &&
                    typeof resolvedRedirectPath === 'string' &&
                    resolvedRedirectPath.length > 0 &&
                    resolvedRedirectPath !== pathname
                ) {
                    const querySuffix = locationSearch && locationSearch !== '?' ? locationSearch : '';
                    return {
                        baseCategoryIds,
                        browseFacetOptionIds,
                        browsePathPairsForPostSearchRemap,
                        redirectUrl: `${resolvedRedirectPath}${querySuffix}`,
                        shouldAttemptPostSearchBrowseRemap,
                        slugOnlyPathForInvalidBrowsePath,
                    };
                }

                if (!resolved.error && resolved.payload?.categoryIds?.length) {
                    baseCategoryIds = resolved.payload.categoryIds;
                    if (!resolved.payload.facetOptionsByFacetId) {
                        const categoryIdBackedFallback = mapResolvedCategoryIdsToBrowseFacetOptions(
                            pathPairsForBrowse,
                            resolved.payload.categoryIds
                        );
                        if (categoryIdBackedFallback) {
                            browseFacetOptionIds = categoryIdBackedFallback;
                        }
                    }
                }

                if (!resolved.error && resolved.payload?.facetOptionsByFacetId) {
                    browseFacetOptionIds = resolved.payload.facetOptionsByFacetId;
                }
            }
        } else {
            if (!browsePathHasTildeMultiValue(pathname)) {
                const resolved = await resolveBrowseUrlSafe(pathname);
                if (!resolved.error && resolved.payload) {
                    if (resolved.payload.categoryIds?.length) {
                        baseCategoryIds = resolved.payload.categoryIds;
                    }
                    if (resolved.payload.facetOptionsByFacetId) {
                        browseFacetOptionIds = resolved.payload.facetOptionsByFacetId;
                    }
                }
            }

            if (!baseCategoryIds.length && slugOnlyPath !== pathname) {
                const slugResolved = await resolveBrowseUrlSafe(slugOnlyPath);
                if (!slugResolved.error && slugResolved.payload?.categoryIds?.length) {
                    baseCategoryIds = slugResolved.payload.categoryIds;
                }
                if (!browseFacetOptionIds && !slugResolved.error && slugResolved.payload?.facetOptionsByFacetId) {
                    browseFacetOptionIds = slugResolved.payload.facetOptionsByFacetId;
                }
            }
        }

        if (!baseCategoryIds.length) {
            const topId = getTopCategoryIdFromSlug(routeContext.params.browseCategorySlug);
            if (topId) {
                baseCategoryIds = [topId];
            }
        }

        if (!baseCategoryIds.length && routeContext.params.browseCategorySlug) {
            const { baseCategoryIds: prevIds, lastBrowseCategorySlug } = stateAfterFacets.browsePath;
            if (
                prevIds.length > 0 &&
                lastBrowseCategorySlug !== null &&
                lastBrowseCategorySlug === routeContext.params.browseCategorySlug
            ) {
                baseCategoryIds = [...prevIds];
            }
        }

        const apiHasAnyFacetIds = hasAnyFacetIds(browseFacetOptionIds);
        if (!apiHasAnyFacetIds) {
            browseFacetOptionIds = pathMappedFacets;
        } else if (pathMappedHasIds) {
            browseFacetOptionIds = { ...browseFacetOptionIds, ...pathMappedFacets };
        }

        if (!hasAnyFacetIds(browseFacetOptionIds) && pathPairsForBrowse.length > 0) {
            shouldAttemptPostSearchBrowseRemap = true;
            browsePathPairsForPostSearchRemap = sortBrowsePairsCanonical(pathPairsForBrowse);
        }

        return {
            baseCategoryIds,
            browseFacetOptionIds,
            browsePathPairsForPostSearchRemap,
            redirectUrl: null,
            shouldAttemptPostSearchBrowseRemap,
            slugOnlyPathForInvalidBrowsePath,
        };
    }

    if (isCombinationCategory) {
        baseCategoryIds = [legacyCategoryId, legacyCategoryId2].filter((id) => !Number.isNaN(id));
    } else if (hasRegularLegacyCategory) {
        baseCategoryIds = [legacyCategoryId];
    }

    return {
        baseCategoryIds,
        browseFacetOptionIds,
        browsePathPairsForPostSearchRemap,
        redirectUrl: null,
        shouldAttemptPostSearchBrowseRemap,
        slugOnlyPathForInvalidBrowsePath,
    };
};
